import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { QUIZ } from "../data/quizData";

const API = process.env.REACT_APP_BACKEND_URL;
const CHOICE_KEYS = ["ithaca", "lefkada", "paliki"];

// Τεστ γνώσεων: οι ερωτήσεις εμφανίζονται πάνω στη γκραβούρα Bellin (1797)
export const QuizPage = () => {
  const { lang } = useLang();
  const q = QUIZ[lang];
  const total = q.questions.length;
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [opinion, setOpinion] = useState(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [tallies, setTallies] = useState(null);

  // Τα συγκεντρωτικά αποτελέσματα του δημοψηφίσματος φορτώνονται πάντα στο τέλος
  useEffect(() => {
    if (!done) return;
    fetch(`${API}/api/referendum/results`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setTallies(d))
      .catch(() => {});
  }, [done]);

  const submitOpinion = async () => {
    setSubmitted(true);
    try {
      const res = await fetch(`${API}/api/referendum/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice: CHOICE_KEYS[q.opinionOptions.indexOf(opinion)], comment }),
      });
      if (res.ok) setTallies(await res.json());
    } catch {
      /* η γνώμη δείχνεται καταγεγραμμένη τοπικά ακόμη κι αν ο server λείπει */
    }
  };

  const current = q.questions[idx];

  const choose = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (i === current.correct) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= total) {
      setDone(true);
    } else {
      setIdx(idx + 1);
      setSelected(null);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const restart = () => {
    setIdx(0);
    setSelected(null);
    setScore(0);
    setDone(false);
    setOpinion(null);
    setComment("");
    setSubmitted(false);
    window.scrollTo(0, 0);
  };

  const pct = score / total;
  const verdict = pct >= 0.8 ? q.verdictHigh : pct >= 0.5 ? q.verdictMid : q.verdictLow;

  return (
    <main data-testid="quiz-main" className="relative min-h-screen">
      {/* Η γκραβούρα της εισαγωγής (Delisle, 1794) ως φόντο */}
      <div className="fixed inset-0 -z-10">
        <img src="/images/dulichium-delisle-1794.jpg" alt="" aria-hidden className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-paper/78" />
      </div>

      <div className="mx-auto max-w-3xl px-6 pb-14 pt-10">
        <div className="rounded-xl bg-paper/85 px-6 py-5 shadow-lg backdrop-blur-sm">
          <h1 data-testid="quiz-title" className="font-serif text-3xl font-semibold tracking-tight text-ink md:text-5xl">
            {q.pageTitle}
          </h1>
        </div>

        {!done ? (
          <div data-testid="quiz-card" className="mt-8 rounded-xl border border-line bg-paper/95 p-6 shadow-xl backdrop-blur-sm md:p-8">
            <p data-testid="quiz-progress" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {idx + 1} / {total}
            </p>
            <h2 className="mt-3 font-serif text-xl font-semibold text-ink md:text-2xl">{current.q}</h2>
            <div className="mt-5 space-y-3">
              {current.options.map((opt, i) => {
                const isSel = selected === i;
                const isOk = i === current.correct;
                const showState = selected !== null;
                return (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`quiz-option-${i}`}
                    onClick={() => choose(i)}
                    className={`w-full rounded-lg border p-4 text-left text-base transition-colors duration-150 ${
                      showState && isOk
                        ? "border-emerald-600 bg-emerald-50 text-emerald-900"
                        : showState && isSel
                          ? "border-red-500 bg-red-50 text-red-900"
                          : "border-line bg-paper hover:border-aegean hover:bg-surface"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {selected !== null && (
              <>
                <div data-testid="quiz-explain" className="mt-5 rounded-lg bg-surface p-4 text-sm leading-relaxed text-muted-foreground">
                  {current.why}
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    data-testid="quiz-next"
                    onClick={next}
                    className="rounded-full bg-aegean px-7 py-3 text-sm font-semibold text-paper transition-colors duration-200 hover:bg-ink"
                  >
                    {idx + 1 >= total ? q.finish : q.next}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div data-testid="quiz-results" className="mt-8 rounded-xl border border-line bg-paper/95 p-6 shadow-xl backdrop-blur-sm md:p-8">
            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{q.resultsTitle}</h2>
            <p data-testid="quiz-score" className="mt-3 font-serif text-4xl text-aegean">
              {score} / {total}
            </p>
            <p className="mt-2 text-muted-foreground">{verdict}</p>

            {score > 35 ? (
              <div className="mt-8 border-t border-line pt-6">
                <h3 data-testid="quiz-opinion-title" className="font-serif text-xl font-semibold text-ink">{q.opinionTitle}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{q.opinionAsk}</p>
                <p data-testid="quiz-rule-note" className="mt-3 rounded-lg bg-aegean/10 p-3 text-xs leading-relaxed text-aegean">
                  {q.opinionRuleUnlocked}
                </p>
                {!submitted ? (
                  <>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      {q.opinionOptions.map((o, i) => (
                        <button
                          key={o}
                          type="button"
                          data-testid={`quiz-opinion-${i}`}
                          onClick={() => setOpinion(o)}
                          className={`rounded-lg border p-3 text-sm font-semibold transition-colors duration-150 ${
                            opinion === o ? "border-terra bg-terra/10 text-ink" : "border-line hover:border-aegean"
                          }`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                    <label htmlFor="quiz-comment" className="mt-4 block text-xs uppercase tracking-widest text-muted-foreground">
                      {q.opinionCommentLabel}
                    </label>
                    <textarea
                      id="quiz-comment"
                      data-testid="quiz-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      className="mt-2 w-full rounded-lg border border-line bg-paper p-3 text-sm"
                    />
                    <button
                      type="button"
                      data-testid="quiz-opinion-submit"
                      disabled={!opinion}
                      onClick={submitOpinion}
                      className="mt-4 rounded-full bg-aegean px-7 py-3 text-sm font-semibold text-paper transition-colors duration-200 hover:bg-ink disabled:opacity-40"
                    >
                      {q.opinionSubmit}
                    </button>
                  </>
                ) : (
                  <p data-testid="quiz-opinion-thanks" className="mt-4 rounded-lg bg-emerald-50 p-4 text-emerald-900">
                    {q.opinionThanks}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-8 border-t border-line pt-6">
                <p data-testid="quiz-rule-locked" className="rounded-lg bg-surface p-4 text-sm leading-relaxed text-muted-foreground">
                  {q.opinionRuleLocked}
                </p>
              </div>
            )}

            {/* Τα σωρευτικά αποτελέσματα του άτυπου δημοψηφίσματος — πάντα ορατά */}
            {tallies && (
              <div data-testid="quiz-referendum-results" className="mt-8 border-t border-line pt-6">
                <h3 className="font-serif text-xl font-semibold text-ink">{q.referendumTitle}</h3>
                <div className="mt-4 space-y-3">
                  {CHOICE_KEYS.map((key, i) => {
                    const n = tallies.counts[key] || 0;
                    const share = tallies.total ? Math.round((n / tallies.total) * 100) : 0;
                    return (
                      <div key={key} data-testid={`quiz-referendum-row-${key}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-ink">{q.opinionOptions[i]}</span>
                          <span className="text-muted-foreground">{n} · {share}%</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-muted">
                          <div className="h-2 rounded-full bg-aegean transition-all duration-700" style={{ width: `${share}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p data-testid="quiz-referendum-total" className="mt-3 text-xs text-muted-foreground">
                  {tallies.total} {q.referendumTotal}
                </p>
              </div>
            )}

            <button
              type="button"
              data-testid="quiz-restart"
              onClick={restart}
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-aegean transition-colors hover:text-terra"
            >
              <RotateCcw className="h-4 w-4" />
              {q.restart}
            </button>
          </div>
        )}
      </div>
    </main>
  );
};
